import { forEach as _forEach, remove as _remove } from '@lodash';
import { REQUEST_TIMEOUT } from '@core/constants';
import Socket from '@core/services/offer/_private/Socket';
import Match from '@core/models/offer/Match';
import { Subscription ,  Observable } from 'rxjs';
import OfferRequest from '@core/services/offer/_private/models/OfferRequest';
import EventSubscriptionCounter from '@core/services/offer/_private/helpers/EventSubscriptionCounter';
import StructManager from '@core/services/common/StructManager';
import EventStreams from '@core/services/offer/_private/helpers/EventStreams';
import { SocketEvents } from '@core/services/offer/_private/models/enums';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import Market from '@models/struct/Market';
import OddType from '@models/struct/OddType';
import Tournament from '@models/struct/Tournament';
import Category from '@models/struct/Category';
import Sport from '@models/struct/Sport';

export default class SocketManager {
    private socket: Socket;
    private eventStreams: EventStreams;

    // helper class which counts number of event subscriptions.  when event doesnt have any subscriptions,
    // manager emits unsubscribe from event to socket
    private eventSubscriptionCounter: EventSubscriptionCounter = new EventSubscriptionCounter();

    // caches all rxjs subscriptions to streams so we can easily unsubsrcibe on stop streaming
    // this is only safety measure, callers should unsubscribe manually
    private streamSubscriptions: Subscription[] = [];

    constructor(private hostServer: string) { }

    public async getEvent(eventId: number, shouldFetchOddResults: boolean = false): Promise<Match> {
        const offerRequest = OfferRequest.createEventRequest(eventId, shouldFetchOddResults);
        this.socket.emit(SocketEvents.getData, offerRequest);
        const events = await this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT, false));
        return events[0];
    }

    public getLiveEvents(startDate: Date, extraParams?: OfferRequest): Promise<Match[]> {
        const offerRequest = OfferRequest.createSocketLiveEventsRequest(startDate);
        this.updateOfferRequest(offerRequest, extraParams);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public getEventsById(eventIds: number[]): Promise<Match[]> {
        const offerRequest = OfferRequest.createEventsByIdRequest(eventIds);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public getEvents(startDate: Date, endDate: Date): Promise<Match[]> {
        const offerRequest = OfferRequest.createAllEventsRequest(startDate, endDate);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public getEventsBySport(sportId: number, startDate: Date, endDate: Date): Promise<Match[]> {
        const offerRequest = OfferRequest.createSportRequest(sportId, startDate, endDate);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public getEventsByTournament(tournamentId: number, startDate: Date, endDate: Date, extraParams?: OfferRequest)
        : Promise<Match[]> {
        const offerRequest = OfferRequest.createTournamentRequest(tournamentId, startDate, endDate);
        this.updateOfferRequest(offerRequest, extraParams);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public getSuperEvents(startDate: Date, endDate: Date): Promise<any> {
        const offerRequest = OfferRequest.createSuperOfferRequest(startDate, endDate);
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getSuperOfferData$(offerRequest));
    }

    public getTopTenEvents(): Promise<Match[]> {
        const offerRequest = OfferRequest.createTopTenRequest();
        this.socket.emit(SocketEvents.getData, offerRequest);
        return this.getData(this.eventStreams.getData$(offerRequest, REQUEST_TIMEOUT));
    }

    public subscribeToStructChangeStream(callback: () => void): Subscription {
        return this.socket.getStructChange$().subscribe(callback);
    }

    public subscribeToEventStream(
        eventId: number,
        callback: (event: Match) => void
    ): Subscription {
        return this.subscribeToEventsStream(
            [eventId],
            (events: Match[]) => {
                if (events.length > 0) {
                    callback(events[0]);
                }
            }
        );
    }

    public subscribeToEventsStream(
        eventIds: number[],
        callback: (events: Match[]) => void
    ): Subscription {
        eventIds.forEach((id) => this.socket.emitSubscriptionToEvent(id));
        eventIds.forEach((id) => this.eventSubscriptionCounter.increment(id));

        const subscription = this.eventStreams.getChanges$(eventIds)
            .subscribe(callback as any);

        this.streamSubscriptions.push(subscription);

        const cleanupSubscription = new Subscription(
            this.cleanupAfterEventUnsubscribe.bind(this, eventIds, subscription));
        cleanupSubscription.add(subscription);
        return cleanupSubscription;
    }

    public subscribeToAllEventChangesStream(callback: (events: Match[]) => void): Subscription {
        return this.eventStreams.getAllChanges$().subscribe(callback);
    }

    public subscribeToLiveEventsCountChangesStream(callback: (eventsCount: number) => void): Subscription {
        return this.eventStreams.getLiveEventCountChanges$().subscribe(callback);
    }

    public subscribeToConnectErrorStream(callback: (error: Error) => void): Subscription {
        return this.socket.getSocketConnectError$().subscribe(callback);
    }

    public subscribeToDisconnectedStream(callback: () => void): Subscription {
        return this.socket.getSocketDisconnected$().subscribe(callback);
    }

    public subscribeToConnectedStream(callback: () => void): Subscription {
        return this.socket.getSocketConnected$().subscribe(callback);
    }

    public subscribeToErrorStream(callback: () => void): Subscription {
        return this.socket.getSocketError$().subscribe(callback);
    }

    public subscribeToReconnectedStream(callback: () => void): Subscription {
        return this.socket.getSocketReconnected$().subscribe(callback);
    }

    public subscribeToMarketGroupEvent(callback: (marketGroup: MarketGroupInfo) => void): void {
        this.socket.getMarketGroup$().subscribe(callback);
    }
    public subscribeToSportEvent(callback: (sport: Sport) => void): void {
        this.socket.getSport$().subscribe(callback);
    }
    public subscribeToCategoryEvent(callback: (category: Category) => void): void {
        this.socket.getCategory$().subscribe(callback);
    }
    public subscribeToTournamentEvent(callback: (tournament: Tournament) => void): void {
        this.socket.getTournament$().subscribe(callback);
    }
    public subscribeToMarketEvent(callback: (market: Market) => void): void {
        this.socket.getMarket$().subscribe(callback);
    }

    public subscribeToOddTypeEvent(callback: (oddType: OddType) => void): void {
        this.socket.getOddType$().subscribe(callback);
    }
    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public startStreaming(): void {
        this.socket = new Socket(this.hostServer);
        this.socket.start();
        this.eventStreams = new EventStreams(
            StructManager.getInstance(),
            {
                liveEventsCountChanges$: this.socket.getLiveEventCountChanges$(),
                allEventChanges$: this.socket.getAllEventChanges$(),
                data$: this.socket.getData$(),
            },
            this.eventSubscriptionCounter
        );
        this.subscribeToReconnectedStream(() => {
            const eventIds = this.eventSubscriptionCounter.getAllEventIds();
            _forEach(eventIds, (eventId) => {
                this.socket.emitSubscriptionToEvent(eventId);
            });
        });
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public stopStreaming(): void {
        [...this.eventSubscriptionCounter.getAllEventIds()]
            .forEach((id) => this.socket.emitRemoveSubscriptionForEvent(id));
        this.eventSubscriptionCounter.clear();
        this.streamSubscriptions = [];
        this.socket.stop();
    }

    public getData(stream: Observable<any>): Promise<any> {
        return new Promise((resolve, reject) => {
            return stream
                .subscribe(
                    (events: any) => resolve(events),
                    (e) => reject(this.eventStreams.transformToAppErrors(e)),
                );
        });
    }

    private cleanupAfterEventUnsubscribe = (eventIds: number[], subscription: Subscription): void => {
        eventIds.forEach((id) => this.eventSubscriptionCounter.decrement(id));

        eventIds.forEach((id) => {
            if (!this.eventSubscriptionCounter.hasSubscriptions(id)) {
                this.socket.emitRemoveSubscriptionForEvent(id);
            }
        });

        _remove(this.streamSubscriptions, (item) => item === subscription);
    };

    private updateOfferRequest(offerRequest: OfferRequest, extraParams?: OfferRequest) {
        if (!extraParams) {
            return;
        }

        Object.assign(offerRequest, extraParams);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/_private/SocketManager.ts